/**
 * API pública del módulo de vídeo (`window.TrackriftVideo`, bundle
 * /v1/video.js). Ver core.ts para el modelo de medición.
 *
 * Instalación en cualquier landing, después del snippet de trackrift:
 *
 *   <script async src="https://t.dominio.com/v1/u.js?id=PUBLIC_ID"></script>
 *   <script async src="https://t.dominio.com/v1/video.js"></script>
 *
 * A partir de ahí se instrumentan solos: `<video>`, iframes de YouTube
 * (incluidos DIRECTOS) e iframes de Vimeo — también los que aparezcan
 * después (MutationObserver). Se excluye cualquiera con [data-track-ignore].
 *
 * Atributos declarativos sobre el elemento:
 *   data-video-id, data-video-title   identificadores estables en el reporting
 *   data-video-live                   fuerza modo directo (sin % de avance)
 *   data-webinar-id                   agrupa varios players de un mismo evento
 *   data-webinar-starts-at            ISO; mide puntualidad de conexión
 *
 * Para players sin <video> ni iframe conocido (VSL propia, Wistia, un
 * <div> con JS a medida) queda la API manual:
 *
 *   const v = TrackriftVideo.custom('vsl-home', { durationSeconds: 420 });
 *   v.play(0); v.progress(37); v.pause(); v.complete();
 *
 * Y para directos/webinars, donde no hay porcentaje que valga:
 *
 *   const w = TrackriftVideo.webinar({ id: 'masterclass-04-08', startsAt: '2026-08-04T19:00:00+02:00' });
 *   w.join(); w.cta('click', 'aplicar');  // w.leave() es automático al salir
 */
import {
  VideoSession,
  activeSessions,
  bindLifecycle,
  config,
  configure,
  observeVisibility,
  type Props,
  type VideoConfig,
} from './core';
import { attach, scanVideos } from './html5';
import { scanYouTube } from './youtube';
import { scanVimeo } from './vimeo';
import { installDebugHook, setDebug } from './debug';

export const VIDEO_SDK_VERSION = '2.0.0';

// ---------------------------------------------------------------------------
// Handles manuales
// ---------------------------------------------------------------------------

export type VideoHandle = {
  /** El player está en pantalla (aún sin play). */
  impression: () => void;
  play: (positionSeconds?: number) => void;
  pause: (positionSeconds?: number) => void;
  /** percent 0–100; secondsWatched si el player lo sabe. */
  progress: (percent: number, secondsWatched?: number) => void;
  seek: (toSeconds: number, fromSeconds?: number) => void;
  complete: (secondsWatched?: number) => void;
  /** CTA/oferta durante el vídeo. */
  cta: (action: 'shown' | 'click', label?: string) => void;
  /** Gate de VSL superado (compat con landings antiguas). */
  unlock: (label?: string) => void;
  setDuration: (seconds: number | null) => void;
  setMuted: (muted: boolean) => void;
  /** Cierra la sesión y manda el resumen. Automático al abandonar la página. */
  exit: (reason?: string) => void;
  session: VideoSession;
};

type CustomMeta = {
  title?: string;
  durationSeconds?: number;
  player?: string;
  live?: boolean;
  webinarId?: string;
  startsAt?: string | number | Date;
  properties?: Props;
};

function toEpoch(v: string | number | Date | undefined): number | null {
  if (v == null) return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const t = Date.parse(v);
  return Number.isNaN(t) ? null : t;
}

/**
 * Sesión manual con reloj virtual: mientras esté "en play", la posición
 * avanza con el reloj de pared. Así un player del que solo sabemos cuándo
 * empieza y cuándo para sigue produciendo permanencia real, heartbeats y
 * hitos de minutos — que es justo lo que necesita un directo.
 */
export function custom(videoId: string, meta: CustomMeta = {}): VideoHandle {
  const session = new VideoSession({
    id: videoId,
    title: meta.title ?? null,
    duration: meta.durationSeconds ?? null,
    player: meta.player ?? (meta.live ? 'live' : 'custom'),
    live: meta.live ?? false,
    webinarId: meta.webinarId ?? null,
    startsAt: toEpoch(meta.startsAt),
    props: meta.properties,
  });

  let playing = false;
  let base = 0;
  let startedAt = 0;
  const pos = () => (playing ? base + (Date.now() - startedAt) / 1000 : base);
  session.probe = () => ({ position: pos(), playing });

  const startFrom = (p: number) => {
    base = p;
    startedAt = Date.now();
    playing = true;
  };

  return {
    session,
    impression: () => session.impression(),
    play: (p = pos()) => {
      startFrom(p);
      session.play(p);
    },
    pause: (p = pos()) => {
      base = p;
      playing = false;
      session.pause(p);
    },
    progress: (percent, secondsWatched) => {
      if (!session.playing) {
        // Un progress sin play previo implica que el vídeo estaba corriendo.
        if (typeof secondsWatched === 'number') startFrom(secondsWatched);
        session.play(pos());
      }
      const dur = session.duration;
      // Con duración conocida el % manda: resincroniza el reloj virtual.
      if (dur && percent >= 0) {
        base = (Math.min(100, percent) / 100) * dur;
        startedAt = Date.now();
      } else if (typeof secondsWatched === 'number' && secondsWatched > pos()) {
        base = secondsWatched;
        startedAt = Date.now();
      }
      session.tick({ position: pos(), playing: true });
    },
    seek: (to, from) => {
      base = to;
      startedAt = Date.now();
      session.seek(to, from ?? null);
    },
    complete: (secondsWatched) => {
      if (typeof secondsWatched === 'number' && secondsWatched > session.watched) {
        session.tick({ position: secondsWatched, playing: true });
      }
      session.complete();
      playing = false;
    },
    cta: (action, label) => session.cta(action, label),
    unlock: (label) => session.unlock(label),
    setDuration: (s) => session.setDuration(s),
    setMuted: (m) => session.setMuted(m),
    exit: (reason = 'manual') => session.exit(reason),
  };
}

export type WebinarOptions = {
  id: string;
  title?: string;
  /** false para una repetición/evergreen con duración conocida. */
  live?: boolean;
  startsAt?: string | number | Date;
  durationSeconds?: number;
  player?: string;
  properties?: Props;
};

/**
 * Sesión de webinar/directo. Igual que `custom` pero en modo live por
 * defecto: sin porcentajes, con hitos de minutos y heartbeats — lo que
 * permite reconstruir permanencia y concurrencia en el dashboard.
 */
export function webinar(opts: WebinarOptions): VideoHandle & { join: (at?: number) => void; leave: (reason?: string) => void } {
  const live = opts.live !== false;
  const h = custom(opts.id, {
    title: opts.title,
    durationSeconds: opts.durationSeconds,
    player: opts.player ?? (live ? 'live' : 'replay'),
    live,
    webinarId: opts.id,
    startsAt: opts.startsAt,
    properties: opts.properties,
  });
  return {
    ...h,
    join: (at = 0) => h.play(at),
    leave: (reason = 'leave') => h.exit(reason),
  };
}

// ---------------------------------------------------------------------------
// Auto-instrumentación
// ---------------------------------------------------------------------------

/** Escanea el DOM: <video> + iframes de YouTube y Vimeo. */
export function scan(root: ParentNode = document): number {
  return scanVideos(root) + scanYouTube(root) + scanVimeo(root);
}

export type TrackriftVideoApi = {
  version: string;
  attach: typeof attach;
  scan: typeof scan;
  custom: typeof custom;
  webinar: typeof webinar;
  configure: (partial: Partial<VideoConfig>) => void;
  debug: (on?: boolean) => void;
  sessions: () => Props[];
};

declare global {
  interface Window {
    TrackriftVideo?: TrackriftVideoApi;
    UDIAVideo?: TrackriftVideoApi;
  }
}

function readScriptConfig(): void {
  const current = document.currentScript as HTMLScriptElement | null;
  const ds = current?.dataset ?? ({} as DOMStringMap);
  const hb = Number(ds.heartbeat);
  if (Number.isFinite(hb) && hb >= 0) config.heartbeatSeconds = hb;
  if (ds.ranges === 'false') config.trackRanges = false;

  let debugOn = ds.debug === 'true';
  try {
    const q = new URLSearchParams(location.search);
    if (q.get('trk_video_debug') === '1' || q.get('trk_debug') === '1') debugOn = true;
  } catch {
    /* noop */
  }
  if (debugOn) setDebug(true);
}

/**
 * Punto de entrada del bundle: expone window.TrackriftVideo, espera al tracker
 * principal (ambos scripts son async, el orden no está garantizado) y
 * auto-instrumenta el DOM, incluidos los players inyectados más tarde.
 */
export function installVideoTracking(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.TrackriftVideo) {
    window.UDIAVideo = window.TrackriftVideo;
    return;
  }

  installDebugHook();
  readScriptConfig();

  window.TrackriftVideo = {
    version: VIDEO_SDK_VERSION,
    attach,
    scan,
    custom,
    webinar,
    configure,
    debug: (on = true) => setDebug(on),
    sessions: () => activeSessions().map((s) => s.snapshot()),
  };
  // Preserve installed integrations while exposing the standalone product API.
  window.UDIAVideo = window.TrackriftVideo;

  const start = () => {
    bindLifecycle();
    scan();
    const mo = new MutationObserver((muts) => {
      for (const m of muts) {
        m.addedNodes.forEach((node) => {
          if (node instanceof HTMLVideoElement) attach(node);
          else if (node instanceof Element) scan(node);
        });
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  };

  const whenDomReady = () => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  };

  // El bundle de vídeo puede cargar antes que el tracker (ambos async).
  let tries = 0;
  const waitTracker = () => {
    const w = window as unknown as { trackrift?: { track?: unknown } };
    if (w.trackrift && typeof w.trackrift.track === 'function') whenDomReady();
    else if (++tries < 80) setTimeout(waitTracker, 250);
    else if (config.debug) console.warn('[trackrift-video] window.trackrift no apareció — ¿falta /v1/u.js?');
  };
  waitTracker();
}

export { attach, configure, setDebug, activeSessions, observeVisibility, VideoSession };
export type { VideoConfig, Props };
